import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'outline'

interface ButtonLinkProps {
    href: string
    children: ReactNode
    variant?: ButtonVariant
    icon?: ReactNode
    className?: string
}

export const buttonClasses = (variant: ButtonVariant = 'primary', className?: string): string => cn(
    'group/button inline-flex h-12 items-center justify-center gap-3 rounded-full pl-6 pr-2 text-sm font-medium tracking-tight transition-all duration-300 disabled:pointer-events-none disabled:opacity-60 md:h-14 md:pl-7 md:text-base',
    variant === 'primary'
        ? 'bg-primary text-primary-foreground hover:brightness-110'
        : 'border border-border bg-white/[0.03] text-foreground hover:border-white/30 hover:bg-white/[0.07]',
    className
)

export const ButtonIcon: FC<{ children?: ReactNode, variant?: ButtonVariant }> = ({ children, variant = 'primary' }): ReactNode => {
    return (
        <span
            aria-hidden='true'
            className={cn(
                'grid size-8 place-items-center rounded-full transition-transform duration-300 group-hover/button:rotate-45 md:size-10',
                variant === 'primary' ? 'bg-white/15' : 'bg-white/10'
            )}
        >
            {children ?? <ArrowUpRight className='size-4' />}
        </span>
    )
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    children,
    variant = 'primary',
    icon,
    className
}): ReactNode => {
    return (
        <Link href={href} className={buttonClasses(variant, className)}>
            {children}

            <ButtonIcon variant={variant}>
                {icon}
            </ButtonIcon>
        </Link>
    )
}
